/**
 * GameOverScreen - Death/Game Over screen
 */

import React, { useState, useEffect } from 'react';
import { playUIClick } from '../../game/audio/SoundEffects';

interface GameOverScreenProps {
  onRestart?: () => void;
  onMainMenu?: () => void;
  nightNumber?: number;
  causeOfDeath?: string;
  survivalTime?: string;
  visible?: boolean;
}

export const GameOverScreen: React.FC<GameOverScreenProps> = ({
  onRestart,
  onMainMenu,
  nightNumber = 1,
  causeOfDeath = 'Caught by a robot',
  survivalTime = '3:24',
  visible = false
}) => {
  const [opacity, setOpacity] = useState(0);
  const [showButtons, setShowButtons] = useState(false);

  useEffect(() => {
    if (visible) {
      // Fade in effect
      setTimeout(() => setOpacity(1), 100);
      // Show buttons after dramatic pause
      setTimeout(() => setShowButtons(true), 2000);
    } else {
      setOpacity(0);
      setShowButtons(false);
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#000000',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'monospace',
      color: '#ffffff',
      zIndex: 3000,
      opacity,
      transition: 'opacity 1s ease',
    }}>
      {/* Static/glitch effect overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'repeating-linear-gradient(0deg, rgba(255, 0, 0, 0.03) 0px, transparent 1px, transparent 2px, rgba(255, 0, 0, 0.03) 3px)',
        opacity: 0.5,
        animation: 'static 0.5s infinite',
        pointerEvents: 'none',
      }} />

      {/* Main content */}
      <div style={{
        textAlign: 'center',
        zIndex: 1,
      }}>
        {/* Game Over text */}
        <h1 style={{
          fontSize: '96px',
          fontWeight: 'bold',
          color: '#ff0000',
          textShadow: '0 0 40px #ff0000, 0 0 80px #ff0000',
          marginBottom: '30px',
          letterSpacing: '8px',
          animation: 'flicker 0.1s infinite',
        }}>
          GAME OVER
        </h1>

        {/* Death info */}
        <div style={{
          marginBottom: '50px',
          fontSize: '18px',
          color: '#aaa',
          lineHeight: '1.8',
        }}>
          <p style={{ marginBottom: '10px' }}>
            <span style={{ color: '#666' }}>Night:</span>{' '}
            <span style={{ color: '#ff0000' }}>{nightNumber}</span>
          </p>
          <p style={{ marginBottom: '10px' }}>
            <span style={{ color: '#666' }}>Time Survived:</span>{' '}
            <span style={{ color: '#ffaa00' }}>{survivalTime}</span>
          </p>
          <p>
            <span style={{ color: '#666' }}>Cause:</span>{' '}
            <span style={{ color: '#ff0000' }}>{causeOfDeath}</span>
          </p>
        </div>

        {/* Buttons */}
        {showButtons && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            alignItems: 'center',
            animation: 'fadeIn 0.5s ease',
          }}>
            <button
              onClick={() => {
                playUIClick();
                onRestart?.();
              }}
              style={{
                padding: '15px 60px',
                fontSize: '24px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                backgroundColor: 'rgba(255, 0, 0, 0.2)',
                color: '#ff0000',
                border: '3px solid #ff0000',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                letterSpacing: '2px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 0, 0, 0.4)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 0, 0, 0.5)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 0, 0, 0.2)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              TRY AGAIN
            </button>

            <button
              onClick={() => {
                playUIClick();
                onMainMenu?.();
              }}
              style={{
                padding: '12px 50px',
                fontSize: '18px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: '#888',
                border: '2px solid #444',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                letterSpacing: '2px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.borderColor = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#888';
                e.currentTarget.style.borderColor = '#444';
              }}
            >
              MAIN MENU
            </button>
          </div>
        )}
      </div>

      {/* Animations */}
      <style>
        {`
          @keyframes flicker {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.9; }
          }

          @keyframes static {
            0% { transform: translateY(0); }
            100% { transform: translateY(10px); }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};
